import { captureException } from "@sentry/browser";
import { useSyncExternalStore } from "react";
import {
  type AccidentReportViewModel,
  VehicleKey,
} from "#accident-statement/schema";
import type { UnauthorizedReportErrorResult } from "../report/auth/UnauthorizedReportErrorResult";
import { type ApiError, api } from "./ApiClient";
import { ConnectionStatus } from "./ConnectionStatus";
import type { SubscriptionCreatedNotification } from "./NotificationTypes";
import type { ReportSubscriptionSubject } from "./ReportSubscriptionSubject";
import signalRManager from "./SignalRManager";
import type { TicketViewModel } from "./TicketViewModel";

interface GetReportWithTicketResult {
  report: AccidentReportViewModel;
  ticket: TicketViewModel<ReportSubscriptionSubject>;
}

interface LoadedReport {
  report: AccidentReportViewModel;
  party: VehicleKey;
  ticket: TicketViewModel<ReportSubscriptionSubject>;
}

interface ConflictResult {
  split: boolean;
}

export type FetchReportResult =
  | { conflict: ConflictResult }
  | { loaded: LoadedReport }
  | { unauthorized: UnauthorizedReportErrorResult }
  | { notFound: true };

async function fetchReport(token: string): Promise<FetchReportResult> {
  try {
    const result = await api.get<GetReportWithTicketResult | null>(
      `report/token/${token}`,
    );

    if (!result) {
      return { notFound: true };
    }

    const report = result.report;
    const party = report && "tokenA" in report ? VehicleKey.A : VehicleKey.B;
    return { loaded: { report, party, ticket: result.ticket } };
  } catch (error: unknown) {
    console.log({ error });
    const apiError = error as ApiError;

    if (apiError.status === 409) {
      const conflictResult = apiError.result as ConflictResult;
      return { conflict: conflictResult };
    }

    if (apiError.status === 401) {
      const unauthorizedResult =
        apiError.result as UnauthorizedReportErrorResult;
      return { unauthorized: unauthorizedResult };
    }

    captureException(apiError);
    throw new Error("Failed to fetch report");
  }
}

class ReportFetcher {
  state: {
    token: string | null;
    fetchResult: FetchReportResult | null;
    isSubscribed: boolean;
    subscriptionLoading: boolean;
    isFetching: boolean;
    isDisconnected: boolean;
  } = {
    token: null,
    fetchResult: null,
    isSubscribed: false,
    subscriptionLoading: false,
    isFetching: false,
    isDisconnected: false,
  };

  private _updateState(state: Partial<ReportFetcher["state"]>) {
    this.state = { ...this.state, ...state };
    this._emit();
  }

  constructor() {
    signalRManager.subscribeToStatus(() => {
      if (
        signalRManager.status === ConnectionStatus.Disconnected ||
        signalRManager.status === ConnectionStatus.Reconnecting
      ) {
        this._updateState({ isSubscribed: false, isDisconnected: true });
      }

      if (signalRManager.status === ConnectionStatus.Connected) {
        if (
          this.state.token &&
          this.state.isDisconnected &&
          !this.state.isFetching &&
          !this.state.subscriptionLoading
        ) {
          void this.load(this.state.token);
        }
      }
    });
  }

  async load(token: string): Promise<void> {
    this._updateState({
      token,
      fetchResult: null,
      isSubscribed: false,
      subscriptionLoading: false,
      isFetching: true,
      isDisconnected: false,
    });

    const fetchResult = await fetchReport(token);

    this._updateState({
      isFetching: false,
      fetchResult,
    });

    if ("loaded" in fetchResult) {
      this._updateState({ subscriptionLoading: true });

      await waitForSignalRConnection();

      const subscriptionEstablishedPromise = waitForSubscriptionEstablished(
        fetchResult.loaded.ticket,
      );

      await signalRManager.sendSubscriptionRequest(
        "ReportSubscriptionSubject",
        fetchResult.loaded.ticket,
      );

      await subscriptionEstablishedPromise;

      this._updateState({
        isSubscribed: true,
        subscriptionLoading: false,
      });
    }
  }

  private _statusListeners: (() => void)[] = [];

  subscribeToStatus(listener: () => void) {
    this._statusListeners.push(listener);

    return () => {
      const index = this._statusListeners.indexOf(listener);
      if (index !== -1) {
        this._statusListeners.splice(index, 1);
      }
    };
  }

  private _emit() {
    for (const listener of this._statusListeners) listener();
  }
}

function waitForSignalRConnection() {
  return new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("Timed out waiting for signalR connection"));
    }, 5000);

    const cleanup = signalRManager.subscribeToStatus(() => {
      if (signalRManager.status === ConnectionStatus.Connected) {
        clearTimeout(timeout);
        cleanup();
        resolve();
      }
    });

    if (signalRManager.status === ConnectionStatus.Connected) {
      clearTimeout(timeout);
      cleanup();
      resolve();
    }
  });
}

function waitForSubscriptionEstablished<TSubject extends object>(
  ticket: TicketViewModel<TSubject>,
) {
  return new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error("Timed out waiting for subscription acknowledgment"));
    }, 5000);

    const cleanup = signalRManager.onNotification(
      "SubscriptionCreatedNotification",
      (data: SubscriptionCreatedNotification) => {
        if (data.ticketId === ticket.ticketId) {
          clearTimeout(timeout);
          cleanup();
          resolve();
        }
      },
    );
  });
}

export const reportFetcher = new ReportFetcher();

const subscribe = (onStoreChange: () => void) =>
  reportFetcher.subscribeToStatus(onStoreChange);

export function useReportFetcherState() {
  return useSyncExternalStore(
    subscribe,
    () => reportFetcher.state,
    () => reportFetcher.state,
  );
}
